import { createApi } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import { PATIENT_STATUSES } from 'features/Home/constants/infoConstants'
import type { IPatientRoom, IPatientRoomWithTimeSlots } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { IPatientSelection } from 'features/Patients/interfaces/IPatient'
import {
  createPatientOptimistic,
  createPatientOptimisticWeek,
  deletePatientOptimistic,
  deletePatientOptimisticWeek,
  getActiveWeekQueries,
  updatePatientOptimistic,
  updatePatientOptimisticWeek,
  updatePatientStatusOptimistic,
  updatePatientStatusOptimisticWeek,
  updateRoomStatusOptimistic,
} from 'features/Schedule/api/optimistics.updates'
import { type PatientAppointmentType } from 'features/Schedule/pusher/type'
import type {
  AvailableSlots,
  ScheduleAppointmentDto,
  ScheduleDay,
  ScheduleDayResponse,
  ScheduleMonth,
  ScheduleWeek,
} from './schedule.types'

export enum SCHEDULE_TAGS {
  SCHEDULE = 'Schedule',
  SCHEDULE_WEEK = 'ScheduleWeek',
}

export const scheduleApi = createApi({
  reducerPath: REDUCERS.SCHEDULE,
  baseQuery: baseQuery(),
  tagTypes: Object.values(SCHEDULE_TAGS),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchScheduleMonth: build.query<
      ScheduleMonth,
      { siteId: string; startDate: string; endDate: string }
    >({
      query: ({ siteId, startDate, endDate }) => ({
        url: `/schedule/month/${startDate}/${endDate}`,
        headers: { 'Database-Connection': siteId },
      }),
      providesTags: [SCHEDULE_TAGS.SCHEDULE],
    }),

    fetchScheduleWeek: build.query<
      ScheduleWeek,
      { siteId: string; startDate: string; endDate: string }
    >({
      query: ({ siteId, startDate, endDate }) => ({
        url: `/schedule/week/${startDate}/${endDate}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (
        response: Omit<ScheduleWeek, 'rooms'> & { rooms: IPatientRoomWithTimeSlots[] },
      ): ScheduleWeek => {
        const rooms = getAdjustedRooms(response.rooms)
        return {
          ...response,
          rooms,
          items: response.items.map((item) => ({
            ...item,
            room: rooms.find((room) => {
              return room.value === item.room.code
            }),
          })) as IPatientSchedule[],
        }
      },
      providesTags: [SCHEDULE_TAGS.SCHEDULE_WEEK],
    }),

    fetchScheduleDay: build.query<ScheduleDay, { siteId: string; date: string }>({
      query: ({ siteId, date }) => ({
        url: `/schedule/day/${date}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: ScheduleDayResponse): ScheduleDay => {
        const rooms = getAdjustedRooms(response.rooms)
        return {
          ...response,
          rooms,
          items: response.items.map((item) => ({
            ...item,
            room: rooms.find((room) => {
              return room.value === item.room.code
            }),
          })) as IPatientSchedule[],
        }
      },
      providesTags: [SCHEDULE_TAGS.SCHEDULE],
    }),

    createScheduleAppointment: build.mutation<
      PatientAppointmentType,
      { siteId: string; date: string; data: ScheduleAppointmentDto }
    >({
      query: ({ siteId, date, data }) => ({
        url: `/schedule/day/${date}/appointment`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data,
      }),
      async onQueryStarted({ siteId, date }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: responseData } = await queryFulfilled
          createPatientOptimistic(dispatch, siteId, date, responseData)

          const state = getState()
          const activeWeekQueries = getActiveWeekQueries(state.schedule.queries, siteId, date)
          activeWeekQueries.forEach(({ startDate, endDate }) => {
            createPatientOptimisticWeek(dispatch, siteId, startDate, endDate, responseData)
          })
        } catch {
          console.log('Something went wrong with createScheduleAppointment')
        }
      },
    }),

    updateScheduleAppointment: build.mutation<
      PatientAppointmentType,
      { siteId: string; date: string; appointmentId: number; data: ScheduleAppointmentDto }
    >({
      query: ({ siteId, date, appointmentId, data }) => ({
        url: `/schedule/day/${date}/appointment/${appointmentId}`,
        method: HTTP_METHODS.PUT,
        headers: { 'Database-Connection': siteId },
        data,
      }),
      async onQueryStarted({ siteId, date }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: responseData } = await queryFulfilled
          updatePatientOptimistic(dispatch, siteId, date, responseData)

          const state = getState()
          const activeWeekQueries = getActiveWeekQueries(state.schedule.queries, siteId, date)
          activeWeekQueries.forEach(({ startDate, endDate }) => {
            updatePatientOptimisticWeek(dispatch, siteId, startDate, endDate, responseData)
          })
        } catch {
          console.log('Something went wrong with updateScheduleAppointment')
        }
      },
    }),

    notifyMultiplePatient: build.mutation<void, { siteId: string; appointmentIds: number[] }>({
      query: ({ siteId, appointmentIds }) => ({
        url: `/schedule/send-notification`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: { appointment_ids: appointmentIds },
      }),
    }),

    deleteScheduleAppointment: build.mutation<
      void,
      { siteId: string; date: string; appointmentId: number; sendSms?: number }
    >({
      query: ({ siteId, date, appointmentId, sendSms = 0 }) => ({
        url: `/schedule/day/${date}/appointment/${appointmentId}/${sendSms}`,
        method: HTTP_METHODS.DELETE,
        headers: { 'Database-Connection': siteId },
      }),
      async onQueryStarted(
        { siteId, date, appointmentId },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          await queryFulfilled
          deletePatientOptimistic(dispatch, siteId, date, appointmentId)

          const state = getState()
          const activeWeekQueries = getActiveWeekQueries(state.schedule.queries, siteId, date)
          activeWeekQueries.forEach(({ startDate, endDate }) => {
            deletePatientOptimisticWeek(dispatch, siteId, startDate, endDate, appointmentId)
          })
        } catch {
          console.log('Something went wrong with deleteScheduleAppointment')
        }
      },
    }),

    checkInPatient: build.mutation<
      { temperature: number },
      { siteId: string; date: string; appointmentId: number; access_code: number | string }
    >({
      query: ({ siteId, appointmentId, access_code }) => ({
        url: `/schedule/check-in-patient/${appointmentId}`,
        headers: { 'Database-Connection': siteId },
        method: HTTP_METHODS.POST,
        data: { access_code },
      }),
      async onQueryStarted(
        { siteId, date, appointmentId },
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data: responseData } = await queryFulfilled
          updatePatientStatusOptimistic(
            dispatch,
            siteId,
            date,
            appointmentId,
            PATIENT_STATUSES.CHECKED_IN_ON_SITE,
            responseData.temperature,
          )

          const state = getState()
          const activeWeekQueries = getActiveWeekQueries(state.schedule.queries, siteId, date)
          activeWeekQueries.forEach(({ startDate, endDate }) => {
            updatePatientStatusOptimisticWeek(
              dispatch,
              siteId,
              startDate,
              endDate,
              appointmentId,
              PATIENT_STATUSES.CHECKED_IN_ON_SITE,
              responseData.temperature,
            )
          })
        } catch {
          console.log('Something went wrong with checkInPatient')
        }
      },
    }),

    notifyPatient: build.mutation<void, { siteId: string; appointmentId: number }>({
      query: ({ siteId, appointmentId }) => ({
        url: `/schedule/notify-patient/${appointmentId}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
      }),
    }),

    updateRoomStatus: build.mutation<
      {
        room_status: {
          code: number
          name: string
        }
        patients: IPatientSelection[]
      },
      {
        siteId: string
        date: string
        roomId: number
        data: {
          status_type_code: number
          lease_id: string
          force_update?: number
        }
      }
    >({
      query: ({ siteId, roomId, data }) => ({
        data,
        url: `/room/${roomId}/status`,
        method: HTTP_METHODS.PUT,
        headers: { 'Database-Connection': siteId },
      }),
      async onQueryStarted({ siteId, date, roomId }, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled
          updateRoomStatusOptimistic(dispatch, siteId, date, roomId, responseData.room_status.code)
        } catch {
          console.log('Something went wrong with updateRoomStatus')
        }
      },
    }),
    updateSelectPatients: build.mutation<
      { data: { status: boolean; message: string } },
      { siteId: string; roomId: number; data: { appointment_ids: number[] } }
    >({
      query: ({ siteId, roomId, data }) => ({
        data,
        url: `/room/${roomId}/appointments`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
      }),
    }),

    findAvailableSlots: build.query<
      AvailableSlots,
      { siteId: string; date: string; for_days: number; duration: number; room_type: number }
    >({
      query: ({ siteId, date, for_days, duration, room_type }) => ({
        url: `/schedule/available-slots`,
        method: HTTP_METHODS.GET,
        headers: { 'Database-Connection': siteId },
        params: { date, for_days, duration, room_type },
      }),
    }),
  }),
})

export const {
  useFetchScheduleMonthQuery,
  useFetchScheduleWeekQuery,
  useFetchScheduleDayQuery,
  useCreateScheduleAppointmentMutation,
  useUpdateScheduleAppointmentMutation,
  useNotifyPatientMutation,
  useNotifyMultiplePatientMutation,
  useDeleteScheduleAppointmentMutation,
  useCheckInPatientMutation,
  useUpdateRoomStatusMutation,
  useUpdateSelectPatientsMutation,
  useLazyFindAvailableSlotsQuery,
} = scheduleApi

const getAdjustedRooms = (rooms: IPatientRoomWithTimeSlots[]): IPatientRoom[] => {
  return rooms.map((room) => ({
    ...room,
    slots: room.time_slots,
    value: String(room.code),
    label: room.name,
    is_waiting_room: !!room.is_waiting_room,
  }))
}
